"use client";

import React, { useState } from "react";
import { SlidersHorizontal, PackageOpen, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  ProductPrice,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ProductsSearch } from "@/components/shop/ProductsSearch";

declare const wvcClient: {
  getCurrentWPQueryParams?: () => Record<string, unknown>;
  getCurrentWPQuery?: () => Record<string, unknown>;
  homeUrl?: () => string;
};

function ProductGridConsumer({ searchTerm }: { searchTerm: string }) {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    total,
  } = useEcommerceProducts();

  const filteredProducts = React.useMemo(() => {
    if (!searchTerm.trim()) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        p.title.toLowerCase().includes(term) ||
        p.categories?.some((c) => c.name.toLowerCase().includes(term))
    );
  }, [products, searchTerm]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((idx) => (
          <div
            key={idx}
            data-index={idx}
            className="flex flex-col rounded-sm border border-border bg-card p-5 animate-pulse"
          >
            <div className="aspect-square w-full rounded-sm bg-muted/40" />
            <div className="mt-4 h-3 w-1/3 rounded-sm bg-muted/40" />
            <div className="mt-2 h-5 w-3/4 rounded-sm bg-muted/40" />
            <div className="mt-4 h-6 w-1/2 rounded-sm bg-muted/40" />
            <div className="mt-6 h-9 w-full rounded-sm bg-muted/40" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-sm border border-border bg-card py-16 px-6 text-center">
        <PackageOpen className="size-12 text-muted-foreground stroke-1" />
        <p className="mt-4 text-base font-medium text-foreground">Catalog sync failed</p>
        <p className="mt-1 text-sm text-muted-foreground">Unable to fetch hardware inventory right now.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="mt-4 rounded-sm font-mono uppercase text-xs"
        >
          Retry Connection
        </Button>
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-sm border border-border bg-card py-16 px-6 text-center">
        <PackageOpen className="size-12 text-muted-foreground stroke-1" />
        <p className="mt-4 text-base font-medium text-foreground">No equipment matched</p>
        <p className="mt-1 text-sm text-muted-foreground">Try adjusting your technical filters or search query.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {filteredProducts.map((product, i) => {
          const categoryName = product.categories?.[0]?.name || "Hardware";
          const inStock = product.is_in_stock ?? true;

          return (
            <article
              key={product.id}
              data-index={i}
              className="group flex flex-col justify-between rounded-sm border border-border bg-card p-5 transition-[border-color,box-shadow] duration-200 hover:border-primary hover:shadow-lg"
            >
              <div>
                <div className="relative aspect-square w-full overflow-hidden rounded-sm bg-muted/20">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      <PackageOpen className="size-10 stroke-1" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <Badge
                      variant={inStock ? "secondary" : "outline"}
                      className="rounded-sm border-border bg-background/90 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground backdrop-blur-xs"
                    >
                      {inStock ? "In Stock" : "Backorder"}
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {categoryName}
                  </span>

                  <h3 className="line-clamp-2 font-serif text-lg font-bold uppercase tracking-tight text-card-foreground transition-colors group-hover:text-primary">
                    <Link to={product.link} className="hover:underline">
                      {product.title}
                    </Link>
                  </h3>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3 border-l-3 border-primary pl-3">
                  <ProductPrice
                    product={product}
                    className="font-serif text-xl font-bold tracking-tight text-foreground"
                  />
                </div>

                <AddToCartButton
                  product={product}
                  productId={product.id}
                  variant="default"
                  size="default"
                  className="w-full"
                  buttonClassName="w-full rounded-sm bg-primary text-primary-foreground font-serif uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-colors"
                  showQuantity={false}
                  text="Add to Cart"
                />
              </div>
            </article>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Page {currentPage} of {totalPages} ({total} Items)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={prevPage}
              disabled={!hasPrev}
              className="rounded-sm border-border bg-card px-3 font-mono text-xs uppercase text-foreground disabled:opacity-40"
            >
              <ChevronLeft className="mr-1 size-4" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextPage}
              disabled={!hasNext}
              className="rounded-sm border-border bg-card px-3 font-mono text-xs uppercase text-foreground disabled:opacity-40"
            >
              Next
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Listings() {
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const wp_query = React.useMemo(() => {
    if (typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQueryParams === "function") {
      const currentParams = wvcClient.getCurrentWPQueryParams();
      return new WP_Query(currentParams);
    }
    return new WP_Query({ post_type: "product", posts_per_page: 12 });
  }, []);

  return (
    <section
      id="product-listings"
      data-nav="dark"
      className="bg-background py-12 text-foreground lg:py-16"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-8">
            <aside className="hidden w-72 shrink-0 lg:block">
              <div className="sticky top-24 rounded-sm border border-border bg-card p-6 shadow-sm">
                <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-2.5">
                    <span className="h-4 w-1 bg-primary" />
                    <h2 className="font-serif text-lg font-bold uppercase tracking-wider text-card-foreground">
                      Specifications
                    </h2>
                  </div>
                </div>
                <ProductsFilter />
              </div>
            </aside>

            <div className="w-full flex-1 space-y-6">
              <div className="flex flex-col gap-4 rounded-sm border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 items-center gap-3">
                  <div className="block lg:hidden">
                    <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                      <SheetTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 rounded-sm border-border bg-secondary font-mono text-xs uppercase text-secondary-foreground"
                        >
                          <SlidersHorizontal className="size-4 text-primary" />
                          Filter
                        </Button>
                      </SheetTrigger>
                      <SheetContent
                        side="left"
                        className="w-full max-w-xs border-r border-border bg-card p-6 text-card-foreground overflow-y-auto"
                      >
                        <SheetHeader className="mb-6 border-b border-border pb-4 p-0">
                          <SheetTitle className="font-serif text-xl font-bold uppercase tracking-wider text-card-foreground flex items-center gap-2">
                            <span className="h-4 w-1 bg-primary" />
                            Filters
                          </SheetTitle>
                        </SheetHeader>
                        <ProductsFilter />
                      </SheetContent>
                    </Sheet>
                  </div>

                  <div className="w-full max-w-sm">
                    <ProductsSearch
                      value={searchValue}
                      onChange={setSearchValue}
                      placeholder="Search equipment..."
                      className="w-full [&_input]:h-9 [&_input]:rounded-sm [&_input]:border-border [&_input]:bg-input [&_input]:font-sans [&_input]:text-sm [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 sm:justify-end">
                  <div className="flex items-center gap-2">
                    <ProductSortDropdown label="Sort:" />
                  </div>
                </div>
              </div>

              <ProductGridConsumer searchTerm={searchValue} />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}